import React, { useState, useCallback, useContext, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from "react-i18next";
import {
    Card,
    CardContent,
    IconButton,
    Tooltip,
    makeStyles
} from '@material-ui/core';
import ServerTable from 'components/ServerTable';
import SecureFetchContext from 'context/SecureFetchContext';
import {
    Package as PackageIcon
} from 'react-feather';
import getApiRoute from 'routes/apiRoutes';
import AppContext from 'context/AppContext';
import {useNavigate} from 'react-router-dom';
import {getShipperIcon, isUser} from 'helpers';
import { NavLink as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
    root: {},
    row: {
        '&:hover': {
            cursor: 'default'
        }
    }
}));

const ParcelshopTable = ({ className, ...rest }) => {
    const classes = useStyles();
    const [rows, setRows] = useState([]);
    const [rowsLength, setRowsLength] = useState(0);
    const [loading, setLoading] = useState(true);
    const [stateCodes, setStateCodes] = useState([]);
    const { t } = useTranslation();
    const secureFetch = useContext(SecureFetchContext);
    const { appValues } = useContext(AppContext);
    let navigate = useNavigate();

    useEffect(() => {
        secureFetch(getApiRoute('parcelshop_state_codes')).then(json => {
            setStateCodes(json.map(code => ({ id: code.toLowerCase(), name: code })));
        }).catch(error => {
            console.error('Error loading parcelshop state codes:', error);
        });
    }, [secureFetch]);

    const getUserShippers = useCallback(() => {
        return [...new Map(
            appValues.user.user_shipper_price_lists.map(pl => [pl.shipper.shortcut, pl.shipper])
        ).values()];
    }, [appValues.user]);

    const getShipperChoices = useCallback(() => {
        const shippers = isUser(appValues.user) ? getUserShippers() : [
            { shortcut: 'GLS' }, 
            { shortcut: 'SPS' }, 
            { shortcut: 'PACKETA' },
            { shortcut: '123' }, 
            { shortcut: 'DPD' }, 
            { shortcut: 'QDL' }
        ];
        return shippers.map(s => ({ id: s.shortcut, name: s.shortcut }));
    }, [appValues.user, getUserShippers]);

    const handleTableUpdate = useCallback((params) => {
        setLoading(true);

        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('page', params.page + 1); // API expects 1-based page index
        queryParams.append('limit', params.rows_per_page);

        // Add sorting parameters if needed
        if (params.order_by) {
            queryParams.append('order_by', params.order_by);
            queryParams.append('order', params.order);
        }

        // Handle shipper filter separately - array values from multiselect
        if (Array.isArray(params.shipper) && params.shipper.length > 0) {
            params.shipper.forEach(s => {
                queryParams.append('shipper[]', s);
            });
        } else if (isUser(appValues.user)) {
            // No shipper filter selected - send all user's shippers
            getUserShippers().forEach(s => {
                queryParams.append('shipper[]', s.shortcut);
            });
        }

        // Add filter parameters (exclude shipper - handled above)
        Object.keys(params).forEach(key => {
            if (!['page', 'rows_per_page', 'order_by', 'order', 'shipper'].includes(key)) {
                queryParams.append(key, params[key]);
            }
        });

        secureFetch(`${getApiRoute('parcelshop_list_paginated')}?${queryParams.toString()}`).then(json => {
            setRows(json.items);
            setRowsLength(json.pagination.total);
            setLoading(false);
        }).catch(error => {
            console.error('Error loading parcelshops:', error);
            setLoading(false);
        });
    }, [secureFetch, appValues.user, getUserShippers]);

    const settings = {
        rows: rows,
        rowsLength: rowsLength,
        loading: loading,
        handleTableUpdate: handleTableUpdate,
        handleRowClick: null,
        rowClass: () => classes.row,
        colOrder: {
            order: 'asc',
            order_by: 'city'
        }, 
        headCells: [
            { id: 'shipper', numeric: false, label: t('label.shipper'), search: true, searchType: 'multiselect', selectChoices: getShipperChoices(), render: (data) => getShipperIcon(data) },
            { id: 'parcelshop_id', numeric: false, label: t('label.id'), search: true },
            { id: 'name', numeric: false, label: t('label.title'), search: true },
            { id: 'street', numeric: false, label: t('label.street'), search: true },
            { id: 'zip', numeric: false, label: t('label.zip'), search: true },
            { id: 'city', numeric: false, label: t('label.city'), search: true },
            { id: 'state_code', numeric: false, label: t('label.state'), search: true, searchType: 'select', selectChoices: stateCodes },
            { id: 'actions', numeric: false, label: t('label.actions'), search: false, hide: appValues.user.roles.includes('ROLE_ADMIN'), render: (data) => {
                return (
                    <Tooltip title={t('tooltip.new_package')} aria-label={t('tooltip.new_package')}>
                        <IconButton
                            color="inherit"
                            component={RouterLink}
                            to={"/app/package/newparcelshop/" + data.shipper_id + '/' + data.id}
                            onClick={null}
                        >
                            <PackageIcon />
                        </IconButton>
                    </Tooltip>
                );
            } },
        ],
    };

    return (
        <Card
            className={clsx(classes.root, className)}
            {...rest}
        >
            <CardContent>
                <ServerTable settings={settings} />
            </CardContent>
        </Card>
    );
}

export default ParcelshopTable;